// Copyright (c) StrangeBee 2023

import React from 'react';

import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { ICONS } from '@Constants/iconsApp';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { THList } from '@Molecules/THList';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Space } from 'antd';
import { OrganisationOutput, OrganisationLinksOutput, SharingProfileOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';

import './OrganisationLinkList.scss';

interface OrganisationLinkListParams {
    data: OrganisationOutput;
    links: OrganisationLinksOutput[];
    orgList: OrganisationOutput[];
    sharingProfileList: SharingProfileOutput[];
}

export const OrganisationLinkList = ({ links, orgList, sharingProfileList, data }: OrganisationLinkListParams) => {
    const locale = useLocalMessage();

    const columns = columnsRenderer<OrganisationLinksOutput>([
        {
            className: 'links-list__column-name',
            render: ({ toOrganisation }) => {
                const org = orgList?.find(({ name }) => name === toOrganisation);

                return (
                    <Space>
                        <UserAvatar name={org?.name} avatar={org?.avatar} size="large" />
                        {toOrganisation}
                    </Space>
                );
            },
        },
        {
            className: 'links-list__column-link-type',
            render: ({ linkType, toOrganisation }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === linkType);

                const Content = (
                    <ColorLabel
                        color="primary-color"
                        label={
                            <div className="organisation-drawer__links__item">
                                {data.name}
                                <ICONS.ArrowRightOutlined />
                                {toOrganisation} ({linkType})
                            </div>
                        }
                    />
                );

                return (
                    <THTooltip
                        title={
                            sharingProfile?.autoShare
                                ? locale('organisation.links.form.link-type.help.shared', {
                                      currentOrganisation: data.name || '',
                                      otherOrganisation: toOrganisation || '',
                                      profile: sharingProfile?.permissionProfile,
                                  })
                                : locale('organisation.links.form.link-type.help.not-shared', {
                                      currentOrganisation: data.name || '',
                                      otherOrganisation: toOrganisation || '',
                                  })
                        }
                    >
                        {Content}
                    </THTooltip>
                );
            },
        },
        {
            className: 'links-list__column-other-link-type',
            render: ({ otherLinkType, toOrganisation }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === otherLinkType);

                const Content = (
                    <ColorLabel
                        color="primary-color"
                        label={
                            <div className="organisation-drawer__links__item">
                                {data.name}
                                <ICONS.ArrowLeftOutlined />
                                {toOrganisation} ({otherLinkType})
                            </div>
                        }
                    />
                );

                return (
                    <THTooltip
                        title={
                            sharingProfile?.autoShare
                                ? locale('organisation.links.form.other-link-type.help.shared', {
                                      currentOrganisation: data.name || '',
                                      otherOrganisation: toOrganisation || '',
                                      profile: sharingProfile?.permissionProfile,
                                  })
                                : locale('organisation.links.form.other-link-type.help.not-shared', {
                                      currentOrganisation: data.name || '',
                                      otherOrganisation: toOrganisation || '',
                                  })
                        }
                    >
                        {Content}
                    </THTooltip>
                );
            },
        },
    ]);

    return (
        <THList>
            <THList.Content
                className="links-list"
                dataSource={links.sort(({ toOrganisation: orgA = '' }, { toOrganisation: orgB = '' }) =>
                    orgA.localeCompare(orgB),
                )}
                columns={columns}
                rowKey="toOrganisation"
                size="small"
            />
        </THList>
    );
};
